import { IOutput, IOutputValue, clone, IOutputGroup, OutputsHelper, EOutputScope } from '../../helpers/';
import types from '../types';

export interface ISummaryDialogState {
    outputs: IOutputValue[];
    globalOutputs: IOutputValue[];
    groups: IOutputGroup[];
    fromId: string;
    overId: string;
    fromGroupIndex: number;
    overGroupIndex: number;
}

interface IAction {
    type: string;
    output?: IOutputValue;
    outputs?: IOutputValue[];
    globalOutputs?: IOutputValue[];
    index?: number;
    toIndex?: number;
    id?: string;
    toId?: string;
}

export const initialState: ISummaryDialogState = {
    outputs: [],
    globalOutputs: [],
    groups: [],
    fromId: null,
    overId: null,
    fromGroupIndex: null,
    overGroupIndex: null
};

const summaryDialogReducer = function(state: ISummaryDialogState = initialState, action: IAction): ISummaryDialogState {

	let stateClone: ISummaryDialogState = clone(state);

	switch(action.type) {

        case types.UPDATE_SUMMARY_OUTPUT:
            stateClone = updateOutput(stateClone, action.output);
            break;

        case types.SET_MOVE_FROM_SUMMARY:
            stateClone = setMoveFrom(stateClone, action.id);
            break;

        case types.MOVE_SUMMARY_OUTPUT:
            stateClone = move(stateClone, action.toId);
            break;

        case types.SET_MOVE_OVER_SUMMARY:
            stateClone = setMoveOver(stateClone, action.id);
            break;

        case types.SET_MOVE_GROUP_FROM_SUMMARY:
            stateClone = setMoveGroupFrom(stateClone, action.index);
            break;

        case types.MOVE_GROUP_SUMMARY_OUTPUT:
            stateClone = moveGroup(stateClone, action.toIndex);
            break;

        case types.SET_MOVE_GROUP_OVER_SUMMARY:
            stateClone = setMoveGroupOver(stateClone, action.index);
            break;

		case types.SET_SUMMARY_OUTPUTS:
			stateClone = setOutputs(stateClone, action.outputs, action.globalOutputs);
			break;

		default: 
			break;

	}

	return stateClone;

};

const updateOutput = (state: ISummaryDialogState, output: IOutputValue): ISummaryDialogState => {
	
    let isGlobal: boolean = !output.componentId;
    let outputs: IOutputValue[] = isGlobal ? state.globalOutputs : state.outputs;

    let matches: {output: IOutputValue, index: number}[] = outputs
		.map((o, i) => {
			return { output: o, index: i };
		})
		.filter(o => o.output.Id === output.Id);

    if (!matches.length)
        return state;
    
	outputs[matches[0].index] = output;

    if (matches[0].output.Group !== output.Group)
        state = updateGroup(state, matches[0].output, output);

    return state;
};

const updateGroup = (state: ISummaryDialogState, previousOutput: IOutputValue, output: IOutputValue): ISummaryDialogState => {

    let { Id } = output;
    let previousGroupName: string = previousOutput.Group;
    let nextGroupName: string = output.Group;

    let previousGroup: IOutputGroup = state.groups
        .filter(g => g.name === previousGroupName)[0];
    let nextGroup: IOutputGroup = state.groups
        .filter(g => g.name === nextGroupName)[0];
    
    if (previousGroup)
        previousGroup.outputs = previousGroup.outputs.filter(id => id !== Id);

    if (nextGroup)
        nextGroup.outputs.push(Id);
    else
        state.groups.push({
            name: nextGroupName,
            outputs: [Id],
            first: output.Order
        });

    state.groups = state.groups.filter(g => g.outputs.length);

    return state;

};

const setMoveFrom = (state: ISummaryDialogState, fromId: string): ISummaryDialogState => {

    state.fromId = fromId;
    state.fromGroupIndex = null;
    return state;

};

const setMoveOver = (state: ISummaryDialogState, id: string): ISummaryDialogState => {

    state.overId = id;
    return state;

};

const cleanMove = (state: ISummaryDialogState): ISummaryDialogState => {
    
    state.fromGroupIndex = null;
    state.overGroupIndex = null;
    state.fromId = null;
    state.overId = null;
    return state;

};

/**
 * Move an output within the same group.
 * @param state {ISummaryDialogState} Original state.
 * @param toId {string} Id of the target output to move to.
 * @return {ISummaryDialogState} Updated state.
 */
const move = (state: ISummaryDialogState, toId: string): ISummaryDialogState => {

    // If no trace of from/to, abort and clean moving properties
    if (toId === null || state.fromId === null)
        return cleanMove(state);

    let { outputs, globalOutputs, fromId, groups } = state;
    let combinedOutputs = [ ...globalOutputs, ...outputs ];
    let fromOutput: IOutputValue = combinedOutputs
        .filter(o => o.Id === state.fromId)[0];
    let toOutput: IOutputValue = combinedOutputs
        .filter(o => o.Id === toId)[0];

    let fromGroup: string = fromOutput && fromOutput.Group;
    let toGroup: string = toOutput && toOutput.Group;

    // Outputs don't exist or groups are different
    if (!fromOutput || !toOutput || fromGroup !== toGroup)
        return cleanMove(state);
    
    let group: IOutputGroup =  state.groups
        .filter(g => g.name === fromGroup)[0];

    let fromIndex: number = group.outputs.indexOf(fromId);
    let toIndex: number = group.outputs.indexOf(toId);

    // Move output in array
    group.outputs.splice(fromIndex, 1);
    group.outputs.splice(toIndex, 0, fromId);
    
    // Clean moving properties and return
    return cleanMove(state);

};

const setMoveGroupFrom = (state: ISummaryDialogState, fromIndex: number): ISummaryDialogState => {

    state.fromGroupIndex = fromIndex;
    state.fromId = null;
    return state;

};

const setMoveGroupOver = (state: ISummaryDialogState, index: number): ISummaryDialogState => {

    state.overGroupIndex = index;
    return state;

};

/**
 * Move a group of outputs in the list of groups.
 * @param state {ISummaryDialogState} Original state.
 * @param toIndex {number} Target group index to move to.
 * @return {ISummaryDialogState} Updated state.
 */
const moveGroup = (state: ISummaryDialogState, toIndex: number): ISummaryDialogState => {

    if (toIndex === null || state.fromGroupIndex === null)
        return cleanMove(state);

    let { outputs, globalOutputs, fromGroupIndex, groups } = state;
    let group: IOutputGroup = { ...groups[fromGroupIndex] };
    
    groups.splice(fromGroupIndex, 1);
    groups.splice(toIndex, 0, group);
    
    return cleanMove(state);

};

const setOutputs = (state: ISummaryDialogState, outputs: IOutputValue[], globalOutputs: IOutput[]): ISummaryDialogState => {

    state.outputs = outputs;
    state.globalOutputs = globalOutputs
        .map(o => {
            return { ...o, componentId: null, OutputScope: EOutputScope.Global };
        });
    
    let combinedOutputs: IOutputValue[] = [ ...state.globalOutputs, ...state.outputs ];

    state.groups = OutputsHelper.GetGroups(combinedOutputs);

    return state;

};

export default summaryDialogReducer;


// WEBPACK FOOTER //
// ./source/ts/store/reducers/summary-dialog.ts